import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../styles/theme';
import Button from './ui/Button';
import { FaCheck } from 'react-icons/fa';

interface Plan {
  id: number;
  name: string;
  tier: string;
  price_monthly: number;
  link_limit: number;
  features: string[];
}

const PricingPage: React.FC = () => {
  const { subscription } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.subscription.getPlans();
      if (response.success) {
        // Only show Free and Pro plans
        const filteredPlans = response.plans.filter((plan: Plan) =>
          plan.tier === 'free' || plan.tier === 'pro'
        );
        setPlans(filteredPlans);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planTier: string) => {
    setProcessingPlan(planTier);
    setError(null);

    try {
      const response = await api.subscription.createCheckoutSession(planTier);

      if (response.success && response.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  const isCurrentPlan = (planTier: string) => {
    return subscription?.planTier === planTier;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        color: theme.colors.text.secondary
      }}>
        Loading plans...
      </div>
    );
  }

  return (
    <div style={{
      padding: '48px 24px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h1 style={{
          fontSize: theme.typography.sizes['3xl'],
          fontWeight: theme.typography.weights.bold,
          color: theme.colors.text.primary,
          marginBottom: '16px'
        }}>
          Choose Your Plan
        </h1>
        <p style={{
          fontSize: theme.typography.sizes.lg,
          color: theme.colors.text.secondary,
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          Start with our free plan and upgrade as you grow. All plans include our core features.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Current Plan Info */}
      {subscription && (
        <div style={{
          padding: '20px',
          backgroundColor: theme.colors.background,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          marginBottom: '32px',
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text.secondary
          }}>
            Current Plan: <strong style={{ color: theme.colors.text.primary }}>
              {subscription.planTier.charAt(0).toUpperCase() + subscription.planTier.slice(1)}
            </strong> •
            {subscription.linkLimit === -1 ? ' Unlimited' : ` ${subscription.linkCount} / ${subscription.linkLimit}`} links used
          </p>
        </div>
      )}

      {/* Plans Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '32px',
        marginBottom: '48px'
      }}>
        {plans.map((plan) => {
          const isCurrent = isCurrentPlan(plan.tier);
          const isProcessing = processingPlan === plan.tier;

          return (
            <div
              key={plan.id}
              style={{
                backgroundColor: theme.colors.surface,
                border: `2px solid ${isCurrent ? theme.colors.primary : theme.colors.border}`,
                borderRadius: '12px',
                padding: '32px',
                position: 'relative',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ...(plan.tier === 'pro' ? {
                  transform: 'scale(1.05)',
                  boxShadow: theme.shadows.lg
                } : {})
              }}
            >
              {/* Current Plan Badge */}
              {isCurrent && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '24px',
                  backgroundColor: theme.colors.primary,
                  color: '#FFFFFF',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: theme.typography.sizes.sm,
                  fontWeight: theme.typography.weights.semibold
                }}>
                  Current Plan
                </div>
              )}

              {/* Recommended Badge */}
              {plan.tier === 'pro' && !isCurrent && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '24px',
                  backgroundColor: theme.colors.accent,
                  color: '#FFFFFF',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: theme.typography.sizes.sm,
                  fontWeight: theme.typography.weights.semibold
                }}>
                  Recommended
                </div>
              )}

              {/* Plan Name */}
              <h3 style={{
                fontSize: theme.typography.sizes['2xl'],
                fontWeight: theme.typography.weights.bold,
                color: theme.colors.text.primary,
                marginBottom: '8px'
              }}>
                {plan.name}
              </h3>

              {/* Price */}
              <div style={{ marginBottom: '24px' }}>
                <span style={{
                  fontSize: theme.typography.sizes['4xl'],
                  fontWeight: theme.typography.weights.bold,
                  color: theme.colors.text.primary
                }}>
                  ${Number(plan.price_monthly).toFixed(0)}
                </span>
                <span style={{
                  fontSize: theme.typography.sizes.base,
                  color: theme.colors.text.secondary
                }}>
                  /month
                </span>
              </div>

              {/* Link Limit */}
              <p style={{
                fontSize: theme.typography.sizes.base,
                color: theme.colors.text.secondary,
                marginBottom: '24px',
                paddingBottom: '24px',
                borderBottom: `1px solid ${theme.colors.border}`
              }}>
                {plan.link_limit === -1 ? 'Unlimited' : `Up to ${plan.link_limit}`} trackable links
              </p>

              {/* Features */}
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 32px 0'
              }}>
                {plan.features.map((feature, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ color: theme.colors.primary, marginTop: '2px' }}>
                      {FaCheck({ size: 16 })}
                    </div>
                    <span style={{
                      fontSize: theme.typography.sizes.base,
                      color: theme.colors.text.primary
                    }}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                variant={plan.tier === 'pro' ? 'primary' : 'secondary'}
                size="lg"
                onClick={() => handleUpgrade(plan.tier)}
                disabled={isCurrent || isProcessing || plan.tier === 'free'}
                style={{ width: '100%' }}
              >
                {isProcessing ? 'Processing...' :
                 isCurrent ? 'Current Plan' :
                 plan.tier === 'free' ? 'Free Forever' :
                 'Upgrade Now'}
              </Button>
            </div>
          );
        })}
      </div>

      {/* FAQ or Additional Info */}
      <div style={{
        textAlign: 'center',
        padding: '32px',
        backgroundColor: theme.colors.background,
        borderRadius: '8px'
      }}>
        <p style={{
          fontSize: theme.typography.sizes.base,
          color: theme.colors.text.secondary,
          margin: 0
        }}>
          Need help choosing a plan? <a href="mailto:support@revenuesocial.com" style={{
            color: theme.colors.primary,
            textDecoration: 'none',
            fontWeight: theme.typography.weights.semibold
          }}>Contact us</a>
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
